<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, reactive } from 'vue';
import { useRouter } from 'vue-router';
import { Icon } from '@iconify/vue';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';
import type { OfficerDetail } from '@/features/AI-documents/types/docGen.types';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import { getTaskConfigByCode } from '@/features/startTask/api/taskConfigApi';
import { saveEFilingData } from '@/features/startTask/api/taskApi';
import {
  WithdrawDirectorForm,
  createEmptyWithdrawDirectorForm,
  toWithdrawDirectorTemplateVariables,
  buildWithdrawDirectorTaskFormData,
  isWithdrawDirectorFormComplete,
  buildWithdrawDirectorPresetPrepareDocuments,
  buildAppointedOfficerSignerJson,
  type WithdrawDirectorFormModel,
} from '@/features/startTask/preset_task/sg/components';

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  selectedPerson?: OfficerDetail | null;
  activePersonContext?: Record<string, any> | null;
  /** Edit 모드 — 값이 있으면 기존 task 수정(Update). 호스트가 restoreState 로 입력 복원. */
  editTaskId?: number | null;
}>();

// Edit(수정) 모드 — 버튼 라벨 'Update task' + entity 자동 prefill skip(restoreState 가 채움) + create→update 분기.
const editMode = computed(() => !!props.editTaskId);
// restoreState 직후 입력 스냅샷(기준선). Update task 는 실제 변경이 있을 때만 활성화.
const editBaseline = ref<string | null>(null);
const isDirty = computed(() => {
  if (!editMode.value) return true; // Create 모드는 항상 활성
  if (editBaseline.value == null) return false; // 기준선 캡처 전 — 비활성
  try { return JSON.stringify(gatherState()) !== editBaseline.value; } catch { return true; }
});

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'create-task', data: { taskTitle: string }): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'change-entity'): void;
  (e: 'change-task-title'): void;
  (e: 'request-person-lookup', context: Record<string, any>): void;
}>();

const router = useRouter();
const creating = ref(false);
const progress = ref('');

// 진행 상태를 호스트(PreSetDocGenView)의 공통 중앙 오버레이로 흘려보낸다.
const presetProgress = usePresetProgress();
watch(creating, (v) => { presetProgress.busy = v; });
watch(progress, (v) => { presetProgress.message = v; });

const taskConfigSteps = ref<string[]>([]);
const effectiveTaskTitle = computed(() => (props.taskTitle || '').trim());
const formModel = reactive<WithdrawDirectorFormModel>(createEmptyWithdrawDirectorForm());

const icpCd = computed(() => props.entityData?.icpCd || props.entityData?.entityCd || '');
const formComplete = computed(() => isWithdrawDirectorFormComplete(formModel));

function buildAddressFromEntity(ent: Record<string, any>): string {
  const postalCode = String(ent.POSTAL_CODE ?? ent.postalCode ?? '').trim();
  const blockNo = String(ent.BLOCK_NO ?? ent.blockNo ?? ent.blockHouseNo ?? '').trim();
  const streetName = String(ent.STREET_NAME ?? ent.streetName ?? '').trim();
  const addrLevel = String(ent.ADDR_LEVEL ?? ent.addrLevel ?? ent.level ?? '').trim();
  const addrUnit = String(ent.ADDR_UNIT ?? ent.addrUnit ?? ent.unit ?? '').trim();
  const buildingName = String(ent.BUILDING_NAME ?? ent.buildingName ?? ent.buildingEstateName ?? '').trim();
  const entityPostalCode = String(ent.entityPostalCode ?? '').trim();
  const entityBlockNo = String(ent.entityBlockNo ?? '').trim();
  const entityStreetName = String(ent.entityStreetName ?? '').trim();
  const entityAddrLevel = String(ent.entityAddrLevel ?? '').trim();
  const entityAddrUnit = String(ent.entityAddrUnit ?? '').trim();
  const entityBuildingName = String(ent.entityBuildingName ?? '').trim();

  const finalBlockNo = blockNo || entityBlockNo;
  const finalStreetName = streetName || entityStreetName;
  const finalAddrLevel = addrLevel || entityAddrLevel;
  const finalAddrUnit = addrUnit || entityAddrUnit;
  const finalBuildingName = buildingName || entityBuildingName;
  const finalPostalCode = postalCode || entityPostalCode;

  const line1 = [finalBlockNo, finalStreetName].filter(Boolean).join(' ');
  const line2 = [finalAddrLevel, finalAddrUnit].filter(Boolean).join('-');
  const unit = line2 ? `#${line2}` : '';
  const assembled = [line1, unit, finalBuildingName, finalPostalCode ? `SINGAPORE ${finalPostalCode}` : ''].filter(Boolean).join(', ').trim();
  if (assembled) return assembled;

  const fallback = String(
    ent.companyAddress ??
      ent.address ??
      ent.ADDRESS ??
      ent.ADDR ??
      ent.addr1 ??
      ent.ADDR1 ??
      ent.ADDR_1 ??
      '',
  ).trim();
  return fallback;
}

async function fetchEntityAddressById(entityCd: string): Promise<string> {
  const tryFetch = async (url: string): Promise<any | null> => {
    try {
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) return null;
      const json = await res.json();
      return json ?? null;
    } catch {
      return null;
    }
  };

  const extractAddressFromPayload = (payload: any): string => {
    const candidates = [
      payload,
      payload?.data,
      payload?.data?.data,
      payload?.data?.item,
      Array.isArray(payload?.data?.items) ? payload.data.items[0] : null,
      Array.isArray(payload?.data?.rows) ? payload.data.rows[0] : null,
      Array.isArray(payload?.data?.entities) ? payload.data.entities[0] : null,
      Array.isArray(payload?.items) ? payload.items[0] : null,
      Array.isArray(payload?.rows) ? payload.rows[0] : null,
      Array.isArray(payload?.entities) ? payload.entities[0] : null,
      payload?.item,
      payload?.profile,
      payload?.entity,
      payload?.company,
      payload?.mainContact,
      payload?.registeredAddress,
      payload?.address,
      payload?.accountingDates,
    ].filter(Boolean);
    for (const c of candidates) {
      const built = buildAddressFromEntity(c as Record<string, any>);
      if (built) return built;
    }
    return '';
  };

  try {
    const endpoints = [
      `/api/startTask/common/entity/${encodeURIComponent(entityCd)}`,
      `/api/entity/${encodeURIComponent(entityCd)}`,
      `/api/entity/profile/${encodeURIComponent(entityCd)}`,
    ];
    for (const url of endpoints) {
      const payload = await tryFetch(url);
      if (!payload) continue;
      const address = extractAddressFromPayload(payload);
      if (address) return address;
    }
  } catch {
    /* ignore */
  }
  console.warn('[PreSetWithdrawDirector] address not found for entity', entityCd);
  return '';
}

watch(
  () => props.entityData,
  async (ent) => {
    if (!ent) return;
    // Edit 모드: 저장된 입력을 restoreState 가 채우므로 entity 자동 prefill(회사/주소 덮어쓰기) skip.
    if (editMode.value) return;
    const name = String(ent.companyName || ent.entityName || ent.ENTITY_NAME || '').trim();
    const reg = String(ent.registrationNo || ent.regNo || ent.REG_NO || '').trim();
    let address = String(
      ent.companyAddress || ent.address || buildAddressFromEntity(ent),
    ).trim();

    if (!address) {
      const entityCd = String(ent.entityCd || ent.ENTITY_CD || ent.icpCd || ent.ICP_CD || '').trim();
      if (entityCd) {
        address = await fetchEntityAddressById(entityCd);
      }
    }
    if (name) formModel.companyName = name;
    if (reg) formModel.companyRegistrationNo = reg;
    if (address) formModel.companyAddress = address;
  },
  { immediate: true, deep: true },
);

onMounted(async () => {
  try {
    const taskCode = props.selection.task.TASK_CODE;
    if (taskCode) {
      const cfg = await getTaskConfigByCode(taskCode);
      taskConfigSteps.value = cfg?.processSteps || [];
    }
  } catch (err) {
    console.warn('[PreSetWithdrawDirector] Task config fetch failed:', err);
  }
});

async function handleCreateTask() {
  const title = effectiveTaskTitle.value;
  if (!title) {
    progress.value = 'Error: enter a Task Title in the chat first.';
    return;
  }
  if (!props.entityData) {
    progress.value = 'Error: select an entity in the chat first.';
    return;
  }
  if (!isWithdrawDirectorFormComplete(formModel)) {
    progress.value = 'Error: complete all withdraw director fields below.';
    return;
  }

  const taskTypeCd = props.selection.task.TASK_CODE;
  if (!taskTypeCd) {
    progress.value = 'Error: Task code missing on preset.';
    return;
  }

  creating.value = true;
  progress.value = 'Creating task...';

  try {
    const steps = (taskConfigSteps.value || []).map((s) => String(s).toLowerCase());
    const taskFlow = {
      createDocument: steps.length === 0 ? true : steps.includes('doc'),
      requestTSign: steps.includes('t-sign'),
      proceedWithEFiling:
        steps.includes('e-filing') ||
        steps.includes('efiling') ||
        ['B32101', 'B32102', 'B32103', 'B32104'].includes(String(taskTypeCd).trim()),
    };
    const taskFormData: Record<string, any> = buildWithdrawDirectorTaskFormData(formModel, icpCd.value || '');
    // Edit 복원용 입력 스냅샷 — TASK_DATA.formData.presetForm 에 저장.
    taskFormData.presetForm = gatherState();

    let taskId: number | null = null;
    if (editMode.value) {
      // ── Update 분기 ──
      taskId = Number(props.editTaskId) || null;
      if (!taskId) { progress.value = 'Error: invalid edit task id.'; creating.value = false; return; }
      progress.value = 'Updating task...';
      try {
        await fetch(`/api/startTask/tasks/${taskId}/data`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ taskData: { formData: taskFormData } }),
        });
      } catch (err) {
        progress.value = `Error: ${err instanceof Error ? err.message : 'Task update failed'}`;
        creating.value = false; return;
      }
      // 제목 갱신 (선택)
      try {
        await fetch(`/api/startTask/tasks/${taskId}/title`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ taskTitle: props.taskTitle }),
        });
      } catch { /* title optional */ }
    } else {
      // ── Create 분기 ──
      const taskRes = await fetch('/api/startTask/tasks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          entityCd: icpCd.value || null,
          taskTypeCd,
          taskTypeName: props.selection.task.TASK_NAME,
          taskTitle: title,
          taskFlow,
          officerData: taskFormData,
        }),
      });
      const taskJson = await taskRes.json();
      taskId = taskJson?.data?.taskId || taskJson?.taskId || null;
      if (!taskId) {
        progress.value = `Error: ${taskJson?.error?.message || taskJson?.message || 'Task creation failed'}`;
        creating.value = false;
        return;
      }
    }

    progress.value = 'Saving documents...';
    const commonFormData = toWithdrawDirectorTemplateVariables(formModel);

    try {
      await saveEFilingData(taskId, {
        taskTypeCd,
        entityCd: icpCd.value || null,
        countryCd: 'SG',
        formData: commonFormData as Record<string, unknown>,
        requestPayload: null,
        responseData: null,
        status: 'B36002',
        isSuccess: false,
      });
    } catch (e) {
      console.warn('[PreSetWithdrawDirector] L_TASK_EFILING_DATA 초기 저장 실패', e);
    }

    const docsToSave = buildWithdrawDirectorPresetPrepareDocuments({
      selectionDocuments: props.selection.documents || [],
      commonFormData,
      personName: formModel.companyName || '',
      entityCd: icpCd.value || undefined,
      withdrawingOfficerSignersJson: buildAppointedOfficerSignerJson({
        name: formModel.resignDirectorName,
        email: String(formModel.contactData?.contactEmail || formModel.contactData?.CONTACT_EMAIL || ''),
        role: 'director',
        contactCd: formModel.contactCd,
        contactRegNo: formModel.contactData?.contactRegNo || formModel.contactData?.CONTACT_REG_NO || '',
      }),
    });

    if (docsToSave.length > 0) {
      try {
        const saveRes = await fetch('/api/aiDocuments/incorporation/prepare-documents', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ taskId, documents: docsToSave, replaceExisting: editMode.value }),
        });
        const saveJson = await saveRes.json();
        if (!saveJson?.success) {
          progress.value = `Documents save warning: ${saveJson?.message || 'unknown'}`;
        }
      } catch (err: unknown) {
        console.warn('[PreSetWithdrawDirector] prepare-documents failed:', err);
      }
    }

    try {
      await fetch(`/api/startTask/tasks/${taskId}/document-status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
    } catch {
      /* ignore */
    }

    try {
      await fetch('/api/startTask/preset/use-log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ presetTaskId: props.selection.task.PRESET_TASK_ID, taskId }),
      });
    } catch {
      /* ignore */
    }

    emit('task-created', { taskId, taskTitle: title });
    // MY 프리셋과 동일: 상세는 Document Setup 허브만 열고, 서명자 자동 편집(editDoc)은 쓰지 않음
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : String(err);
    console.error('[PreSetWithdrawDirector] create task failed:', err);
    progress.value = `Error: ${msg}`;
  } finally {
    creating.value = false;
  }
}

// ─── Edit 지원: 전체 입력 스냅샷 gather/restore (호스트가 TASK_DATA.formData.presetForm 로 저장/복원) ───
//   단일 formModel(reactive) 한 곳에 모든 입력이 모여있으므로 통째로 deep-clone.
function gatherState(): Record<string, any> {
  return {
    formModel: JSON.parse(JSON.stringify(formModel)),
  };
}

function restoreState(s: Record<string, any> | null | undefined) {
  if (!s || typeof s !== 'object') return;
  if (s.formModel && typeof s.formModel === 'object') {
    Object.assign(formModel, JSON.parse(JSON.stringify(s.formModel)));
  }
  // 파생 watcher 가 정착한 뒤 기준선 캡처 → 이후 실제 변경만 dirty 로 감지.
  nextTick(() => { editBaseline.value = JSON.stringify(gatherState()); });
}

defineExpose({ gatherState, restoreState });
</script>

<template>
  <div class="wd-panel">
    <div class="wd-header">
      <div>
        <div class="wd-title">{{ selection.task.TASK_NAME }}</div>
        <div class="wd-subtitle">Director&rsquo;s resolution — Withdraw director (Singapore)</div>
      </div>
      <v-btn variant="text" size="small" @click="emit('back')">
        <v-icon start size="16">mdi-arrow-left</v-icon>
        Back
      </v-btn>
    </div>

    <div class="wd-content">
      <div class="wd-task-section">
        <div class="wd-task-section-header">
          <Icon icon="mdi:text-box-outline" width="16" color="#666" />
          <span>Task Title</span>
        </div>
        <div v-if="effectiveTaskTitle" class="d-flex align-start justify-space-between ga-3">
          <div class="wd-readonly-box flex-grow-1">{{ effectiveTaskTitle }}</div>
          <v-btn variant="tonal" color="primary" size="small" @click="emit('change-task-title')">
            <v-icon start size="16">mdi-pencil</v-icon>
            Change
          </v-btn>
        </div>
        <div v-else class="wd-readonly-box wd-readonly-empty">
          Waiting for task title (enter it in the chat on the left)...
        </div>
      </div>

      <div v-if="entityData" class="wd-entity">
        <div class="d-flex align-center ga-3 justify-space-between">
          <div class="d-flex align-center ga-3">
            <div class="wd-entity-icon">
              <Icon icon="mdi:domain" width="24" color="#1B84FF" />
            </div>
            <div>
              <div class="wd-entity-name">{{ formModel.companyName || entityData.companyName || '—' }}</div>
              <div v-if="formModel.companyRegistrationNo || entityData.registrationNo" class="wd-entity-reg">
                UEN / Reg. No.: {{ formModel.companyRegistrationNo || entityData.registrationNo }}
              </div>
            </div>
          </div>
          <v-btn variant="tonal" color="primary" size="small" @click="emit('change-entity')">
            <v-icon start size="16">mdi-swap-horizontal</v-icon>
            Change
          </v-btn>
        </div>
      </div>

      <div v-else class="wd-empty">
        <Icon icon="mdi:link-variant-plus" width="40" color="#d0d0d0" class="mb-2" />
        <div class="wd-empty-title">No entity linked</div>
        <div class="wd-empty-hint">Use the chat on the left to select a company.</div>
      </div>

      <div v-if="entityData" class="wd-form-wrap">
        <WithdrawDirectorForm
          v-model="formModel"
          :documents="selection.documents || []"
          :selected-person="selectedPerson || null"
          :active-person-context="activePersonContext || null"
          @request-person-lookup="(ctx) => emit('request-person-lookup', ctx)"
        />
      </div>
    </div>

    <div v-if="creating || progress" class="wd-progress">
      <v-progress-linear v-if="creating" indeterminate color="primary" height="3" />
      <div class="wd-progress-text" :class="{ 'text-error': progress.startsWith('Error') }">
        {{ progress }}
      </div>
    </div>

    <div class="wd-footer">
      <v-btn variant="outlined" color="grey" size="small" @click="emit('back')">
        <Icon icon="mdi:arrow-left" width="16" class="mr-1" />
        Back
      </v-btn>
      <v-tooltip :disabled="!(editMode && !isDirty)" location="top" text="No changes to update">
        <template #activator="{ props: tProps }">
          <span v-bind="tProps">
            <v-btn
              variant="flat"
              color="primary"
              size="small"
              :disabled="creating || !isReady || !formComplete || (editMode && !isDirty)"
              :loading="creating"
              @click="handleCreateTask"
            >
              <Icon :icon="editMode ? 'mdi:content-save' : 'mdi:check'" width="16" class="mr-1" />
              {{ editMode ? 'Update task' : 'Create task' }}
            </v-btn>
          </span>
        </template>
      </v-tooltip>
    </div>
  </div>
</template>

<style scoped>
.wd-panel { height: 100%; display: flex; flex-direction: column; overflow: hidden; }
.wd-header { display: flex; align-items: center; justify-content: space-between; padding: 20px 24px 12px; border-bottom: 1px solid #f0f0f0; }
.wd-title { font-size: 18px; font-weight: 700; color: #1a1a2e; }
.wd-subtitle { font-size: 12px; color: #888; margin-top: 2px; }
.wd-content { flex: 1; overflow-y: auto; padding: 20px 24px; }
.wd-task-section { margin-bottom: 16px; background: #fff; border: 1px solid #e9edf5; border-radius: 12px; padding: 0 0 12px; overflow: hidden; }
.wd-task-section > :not(.wd-task-section-header) { padding-left: 14px; padding-right: 14px; }
.wd-task-section-header { display: flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 600; color: #283593; margin: 0 0 12px; padding: 10px 14px; background: linear-gradient(180deg, #eef2ff 0%, #e8ecfb 100%); border-bottom: 1px solid #d6dcf2; }
.wd-readonly-box { background: #fafafa; border: 1px solid #eee; border-radius: 6px; padding: 10px 12px; font-size: 13px; color: #333; line-height: 1.5; }
.wd-readonly-empty { color: #bbb; font-style: italic; }
.wd-entity { background: #f0f5ff; border: 1px solid #d6e4ff; border-radius: 12px; padding: 16px; margin-bottom: 16px; }
.wd-entity-icon { width: 40px; height: 40px; border-radius: 10px; background: #fff; display: flex; align-items: center; justify-content: center; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08); }
.wd-entity-name { font-size: 15px; font-weight: 600; color: #1a1a2e; }
.wd-entity-reg { font-size: 12px; color: #666; margin-top: 2px; }
.wd-empty { text-align: center; padding: 28px 16px; border: 1px dashed #ddd; border-radius: 12px; margin-bottom: 16px; }
.wd-empty-title { font-size: 14px; font-weight: 600; color: #666; }
.wd-empty-hint { font-size: 12px; color: #999; margin-top: 4px; }
.wd-form-wrap { margin-bottom: 8px; }
.wd-progress { border-top: 1px solid #f0f0f0; padding: 8px 24px 0; }
.wd-progress-text { font-size: 12px; color: #555; padding: 6px 0 10px; }
.wd-footer { display: flex; justify-content: space-between; align-items: center; padding: 12px 24px 16px; border-top: 1px solid #f0f0f0; }
</style>
